import qs from 'qs';
import { Button } from 'react-bootstrap';
import type { FunctionComponent } from 'react';

import { Category, CATEGORY_TO_ISSUE_TYPES } from '../../../../shared/issuesV2';
import { FREE_PLAN_NAME } from '../../../../shared/subscriptions/consts';
import { usePlanSelectorContext } from '../../../contexts/PlanSelectorContext';
import { useParams, useRouter } from '../../../contexts/RouterContext';
import { getProjectBaseURLFromParams, isAuthed, isProjectInCurrentOrg } from '../../../utils';
import Flex from '../../generic/Flex';
import Icon, { icons } from '../../generic/Icon';
import { useRevisionScanIdQueryParam } from '../hooks/useRevisionScanIdQueryParam';
import type { IssueType } from '../../../../shared/issuesV2';
import type { Project } from '../../../types';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import ISSUE_INDEX from './IssueIndex';

type Props = {
  issueType: IssueType;
  count: number;
  projectOrgId?: Project['organizationId'];
};

/**
 * Renders an issue "item" panel on the project summary page.
 * @param {Props} param0 - The props for the component.
 * @param {IssueType} param0.issueType - The type of issue.
 * @param {number} param0.count - The number of issues.
 * @param {number | undefined} [param0.projectOrgId] - The project organization ID.
 */
const IssueListItem: FunctionComponent<Props> = ({
  issueType,
  count,
  projectOrgId,
}) => {
  const router = useRouter();
  const params = useParams<RevisionRouteParams>();
  const { showPlanSelectorModal } = usePlanSelectorContext();
  const { revisionScanId } = useRevisionScanIdQueryParam();

  const onItemClick = () => {
    if (!isAuthed()) {
      return showPlanSelectorModal({ tier: FREE_PLAN_NAME });
    }
    if (!isProjectInCurrentOrg(projectOrgId ?? 0)) {
      return null;
    }

    const queryParams: Record<string, unknown> = {};

    // filter down to specific revision scan if applicable
    if (revisionScanId) {
      queryParams.revisionScanId = revisionScanId;
    }
    // choose page and query params based on issue type
    let issueDashPage;
    if (CATEGORY_TO_ISSUE_TYPES[Category.Vulnerability].includes(issueType)) {
      issueDashPage = 'vulnerability';
      // Malware is also a Vulnerability-category issue, so filter the security inbox down to it.
      // Plain vulnerabilities keep the unfiltered security inbox (existing behaviour).
      if (issueType !== 'vulnerability') {
        queryParams.filter = {
          type: [issueType],
        };
      }
    } else if (CATEGORY_TO_ISSUE_TYPES[Category.Quality].includes(issueType)) {
      issueDashPage = 'quality';
      queryParams.filter = {
        type: [issueType],
      };
    } else {
      issueDashPage = `licensing`;
      queryParams.filter = {
        type: [issueType],
      };
    }

    // build path from selected issue page and search params from provided query params
    const issueDashPath = `${getProjectBaseURLFromParams(params)}/issues/${issueDashPage}`;

    // redirect to issues page with relevant issues selected
    const issueDashURL = `${issueDashPath}?${qs.stringify(queryParams)}`;
    return router.push(issueDashURL);
  };

  if (!count) {
    return null;
  }

  const issueInfo = ISSUE_INDEX[issueType] || {
    title: 'Issue',
  };

  return (
    <li
      className="issue-list-item upshadow"
      onClick={onItemClick}
    >
      <span className="icon-circle" />
      <Flex align="center" justify="space-between">
        <h4>
          {count}{' '}
          {issueInfo.title || issueType}
        </h4>
        <div className="text-right">
          {!isProjectInCurrentOrg(projectOrgId ?? 0) ? (
            <Button
              bsStyle="default"
              disabled
            >
              <Icon icon={icons.lock} /> Maintainer Access Required
            </Button>
          ) : (
            <Button bsStyle="default">
              <Icon icon={icons.reviewFix} /><span className="margin-left-xs full">Review & Fix</span>
            </Button>
          )}
          <span className="margin-left-xs">
            <Icon icon={icons.chevronDown} />
          </span>
        </div>
      </Flex>
      <p>
        {issueInfo.description}
      </p>
    </li>
  );
};

export default IssueListItem;
